import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { 
    Layout, Save, Clock, Phone, Mail, Instagram, Facebook, Twitter, Youtube, 
    Sparkles, Palette, Utensils, Shield, Truck, Plus, Trash2, ArrowRight, Eye, Image as ImageIcon, MapPin, Type, ExternalLink, Globe, Crosshair, AlertTriangle
} from 'lucide-react';
import Loading from '../../components/Loading';
import { cleanLogoUrl } from '../../utils/logo';

const mapLibraries = ['places'];
const mapContainerStyle = { width: '100%', height: '280px' };

const defaultGalleryItems = [
    { id: 1, title: 'Luxury Main Dining Hall', category: 'ambiance', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80', tag: 'Restaurant Interior' },
    { id: 2, title: 'Romantic Outdoor Garden Patio', category: 'ambiance', image: 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=800&q=80', tag: 'Outdoor Dining' },
    { id: 3, title: 'Executive Cocktail Lounge & Bar', category: 'ambiance', image: 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?auto=format&fit=crop&w=800&q=80', tag: 'Lounge Bar' },
    { id: 4, title: 'Signature Artisan Pizza', category: 'mains', image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80', tag: 'Chef Choice' },
    { id: 5, title: 'Gourmet Smashed Burger', category: 'mains', image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80', tag: 'Best Seller' },
    { id: 6, title: 'Decadent Chocolate Lava Cake', category: 'desserts', image: 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?auto=format&fit=crop&w=800&q=80', tag: 'Sweet Delight' }
];

const defaultGalleryCategories = [
    { id: 'ambiance', label: 'Restaurant Ambiance', icon: '🏛️' },
    { id: 'mains', label: 'Signature Mains', icon: '🍕' },
    { id: 'starters', label: 'Starters & Sides', icon: '🍟' },
    { id: 'desserts', label: 'Desserts', icon: '🍰' },
    { id: 'beverages', label: 'Mocktails & Drinks', icon: '🍹' }
];

export default function HomepageEditor() {
    const toast = useToast();
    const { tenant } = useTenant();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('theme'); // theme, hero, features, gallery, location, hours, contact, legal

    const [primaryColor, setPrimaryColor] = useState('#5b4310');
    const [secondaryColor, setSecondaryColor] = useState('#000000');
    const [logoUrl, setLogoUrl] = useState('');
    const [restaurantName, setRestaurantName] = useState('Savory Bites');
    const [uploading, setUploading] = useState(false);
    const [restaurantMapLocation, setRestaurantMapLocation] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [mapSearchInput, setMapSearchInput] = useState('');
    const [autocompleteInstance, setAutocompleteInstance] = useState(null);

    const { isLoaded: isMapLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        libraries: mapLibraries
    });

    // Helper: update pin, address, and embed URL from lat/lng
    const updateLocationFromCoords = useCallback((lat, lng, address) => {
        setRestaurantMapLocation({ lat, lng });
        if (mapInstance) mapInstance.panTo({ lat, lng });

        const embedUrl = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin`;
        setHomeSettings(prev => ({ ...prev, mapEmbedUrl: embedUrl }));

        if (address) {
            setHomeSettings(prev => ({ ...prev, locationAddress: address }));
            setMapSearchInput(address);
        } else {
            // Reverse geocode
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    setHomeSettings(prev => ({ ...prev, locationAddress: results[0].formatted_address }));
                    setMapSearchInput(results[0].formatted_address);
                }
            });
        }
    }, [mapInstance]);

    const handleMapClick = useCallback((e) => {
        updateLocationFromCoords(e.latLng.lat(), e.latLng.lng());
    }, [updateLocationFromCoords]);

    const handlePlaceSelected = () => {
        if (autocompleteInstance) {
            const place = autocompleteInstance.getPlace();
            if (place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                updateLocationFromCoords(lat, lng, place.formatted_address || place.name);
            }
        }
    };

    const handleCenterOnMe = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                updateLocationFromCoords(position.coords.latitude, position.coords.longitude);
            });
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);
            uploadData.append('type', 'logo');

            const response = await api.post('/api/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.secureUrl) {
                setLogoUrl(response.data.secureUrl);
                toast.success('Logo uploaded successfully');
            } else {
                toast.error('Failed to upload logo');
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            toast.error(err.response?.data?.message || 'Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    const handleGalleryImageUpload = async (e, itemId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);
            uploadData.append('type', 'items');

            const response = await api.post('/api/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.secureUrl) {
                handleGalleryItemChange(itemId, 'image', response.data.secureUrl);
                toast.success('Gallery photo uploaded successfully');
            } else {
                toast.error('Failed to upload photo');
            }
        } catch (err) {
            console.error('Gallery upload error:', err);
            toast.error(err.response?.data?.message || 'Failed to upload photo');
        } finally {
            setUploading(false);
        }
    };

    const handleHeroImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);
            uploadData.append('type', 'items');

            const response = await api.post('/api/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.secureUrl) {
                setHomeSettings(prev => ({ ...prev, heroImage: response.data.secureUrl }));
                toast.success('Hero background image uploaded successfully');
            } else {
                toast.error('Failed to upload hero image');
            }
        } catch (err) {
            console.error('Hero image upload error:', err);
            toast.error(err.response?.data?.message || 'Failed to upload hero image');
        } finally {
            setUploading(false);
        }
    };

    const [homeSettings, setHomeSettings] = useState({
        heroTitle: '',
        heroSubtitle: '',
        heroImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1920&q=80',
        heroTextColor: '#FFFFFF',
        heroSubtitleColor: '#E0E7FF',
        headingTextColor: '#0F172A',
        bodyTextColor: '#475569',
        footerTextColor: '#CBD5E1',
        footerHeadingColor: '#FFFFFF',
        footerBgColor: '#0F172A',
        footerTimeColor: '#fbbf24',
        footerSecondaryColor: '#94A3B8',
        feature1Title: 'Fast Delivery',
        feature1Desc: 'Hot and fresh food delivered within 30 minutes',
        feature2Title: 'Quality Guaranteed',
        feature2Desc: '100% satisfaction guarantee on every order',
        feature3Title: 'Free Delivery',
        feature3Desc: 'Free delivery on orders over ₹499',
        galleryBadge: 'Visual Feast',
        galleryTitle: 'Our Culinary Gallery',
        galleryDesc: 'Take a glimpse into our signature dishes handcrafted daily by executive chefs using fresh organic ingredients.',
        galleryItems: defaultGalleryItems,
        galleryCategories: defaultGalleryCategories,
        locationAddress: '',
        mapEmbedUrl: '',
        fssaiLicense: '12422020000145',
        operatingHours: [
            { day: 'Monday - Thursday', time: '11:00 AM - 10:30 PM', status: 'Open' },
            { day: 'Friday - Saturday', time: '11:00 AM - 11:30 PM', status: 'Open Late' },
            { day: 'Sunday', time: '12:00 PM - 10:00 PM', status: 'Open' }
        ],
        contactPhone: '+91 98765 43210',
        contactEmail: 'contact@savorybites.com',
        instagramUrl: 'https://instagram.com',
        facebookUrl: 'https://facebook.com',
        twitterUrl: 'https://twitter.com',
        youtubeUrl: 'https://youtube.com',
        privacyPolicy: '',
        termsOfService: '',
        refundPolicy: '',
        displaySocialIcons: true,
        showInstagram: true,
        showFacebook: true,
        showTwitter: true,
        showYoutube: true
    });

    useEffect(() => {
        fetchHomepageSettings();
    }, []);

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-600', primaryColor);
            document.documentElement.style.setProperty('--primary-500', primaryColor);
            document.documentElement.style.setProperty('--primary-700', primaryColor);
        }
        if (secondaryColor) {
            document.documentElement.style.setProperty('--secondary-600', secondaryColor);
            document.documentElement.style.setProperty('--secondary-500', secondaryColor);
            document.documentElement.style.setProperty('--secondary-700', secondaryColor);
        }
        const btnText = homeSettings.primaryButtonTextColor || '#ffffff';
        const activeText = homeSettings.sidebarActiveTextColor || primaryColor;
        const activeBg = homeSettings.sidebarActiveBgColor || (primaryColor ? primaryColor + '1a' : 'rgba(91, 67, 16, 0.1)');
        const enableStroke = homeSettings.enableTextStroke === 'yes';
        
        document.documentElement.style.setProperty('--primary-btn-text', btnText);
        document.documentElement.style.setProperty('--sidebar-active-text', activeText);
        document.documentElement.style.setProperty('--sidebar-active-bg', activeBg);
        document.documentElement.style.setProperty('--text-stroke-navbar', enableStroke ? '1px rgba(0, 0, 0, 0.4)' : 'none');
        document.documentElement.style.setProperty('--text-stroke-hero', enableStroke ? '1px rgba(0, 0, 0, 0.9)' : 'none');
    }, [primaryColor, secondaryColor, homeSettings.primaryButtonTextColor, homeSettings.sidebarActiveTextColor, homeSettings.sidebarActiveBgColor, homeSettings.enableTextStroke]);

    const fetchHomepageSettings = async () => {
        try {
            setLoading(true);
            const isFranchiseAdmin = user?.role === 'franchise_admin';
            const endpoint = isFranchiseAdmin ? '/api/franchises/branding' : '/api/restaurants/settings/homepage';
            const res = await api.get(endpoint);
            const data = res.data;
            const hs = typeof data.home_settings === 'string' ? JSON.parse(data.home_settings) : data.home_settings || {};

            setRestaurantName(data.name || 'Savory Bites');
            setPrimaryColor(data.primary_color || '#5b4310');
            setSecondaryColor(data.secondary_color || '#000000');
            setLogoUrl(data.logo_url || '');

            setHomeSettings({
                heroTitle: hs.heroTitle || (data.name ? `Welcome to ${data.name}` : ''),
                heroSubtitle: hs.heroSubtitle || 'Exquisite flavours, handcrafted recipes, and lightning-fast delivery straight to your doorstep.',
                heroImage: hs.heroImage || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1920&q=80',
                heroTextColor: hs.heroTextColor || '#FFFFFF',
                heroSubtitleColor: hs.heroSubtitleColor || '#E0E7FF',
                headingTextColor: hs.headingTextColor || '#0F172A',
                bodyTextColor: hs.bodyTextColor || '#475569',
                footerTextColor: hs.footerTextColor || '#CBD5E1',
                footerHeadingColor: hs.footerHeadingColor || '#FFFFFF',
                footerBgColor: hs.footerBgColor || '#0F172A',
                footerTimeColor: hs.footerTimeColor || '#fbbf24',
                footerSecondaryColor: hs.footerSecondaryColor || '#94A3B8',
                feature1Title: hs.feature1Title || 'Fast Delivery',
                feature1Desc: hs.feature1Desc || 'Hot and fresh food delivered within 30 minutes',
                feature2Title: hs.feature2Title || 'Quality Guaranteed',
                feature2Desc: hs.feature2Desc || '100% satisfaction guarantee on every order',
                feature3Title: hs.feature3Title || 'Free Delivery',
                feature3Desc: hs.feature3Desc || 'Free delivery on orders over ₹499',
                galleryBadge: hs.galleryBadge || 'Visual Feast',
                galleryTitle: hs.galleryTitle || 'Our Culinary Gallery',
                galleryDesc: hs.galleryDesc || 'Take a glimpse into our signature dishes handcrafted daily by executive chefs using fresh organic ingredients.',
                galleryItems: Array.isArray(hs.galleryItems) && hs.galleryItems.length > 0 ? hs.galleryItems : defaultGalleryItems,
                galleryCategories: Array.isArray(hs.galleryCategories) && hs.galleryCategories.length > 0 ? hs.galleryCategories : defaultGalleryCategories,
                locationAddress: hs.locationAddress || data.address || '123 Gourmet Boulevard, Foodie Haven, Chennai, Tamil Nadu',
                mapEmbedUrl: hs.mapEmbedUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.883737482869!2d80.2472!3d13.0475!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDAyJzUxLjAiTiA4MMKwMTQnNDkuOSJF!5e0!3m2!1sen!2sin!4v1650000000000!5m2!1sen!2sin',
                fssaiLicense: hs.fssaiLicense || '12422020000145',
                operatingHours: Array.isArray(hs.operatingHours) && hs.operatingHours.length > 0 ? hs.operatingHours : [
                    { day: 'Monday - Thursday', time: hs.monThuHours || '11:00 AM - 10:30 PM', status: 'Open' },
                    { day: 'Friday - Saturday', time: hs.friSatHours || '11:00 AM - 11:30 PM', status: 'Open Late' },
                    { day: 'Sunday', time: hs.sunHours || '12:00 PM - 10:00 PM', status: 'Open' }
                ],
                contactPhone: hs.contactPhone || data.contact_phone || '+91 98765 43210',
                contactEmail: hs.contactEmail || data.contact_email || 'contact@savorybites.com',
                instagramUrl: hs.instagramUrl || 'https://instagram.com',
                facebookUrl: hs.facebookUrl || 'https://facebook.com',
                twitterUrl: hs.twitterUrl || 'https://twitter.com',
                youtubeUrl: hs.youtubeUrl || 'https://youtube.com',
                privacyPolicy: hs.privacyPolicy || '',
                termsOfService: hs.termsOfService || '',
                refundPolicy: hs.refundPolicy || '',
                primaryButtonTextColor: hs.primaryButtonTextColor || '#ffffff',
                sidebarActiveTextColor: hs.sidebarActiveTextColor || data.primary_color || '#5b4310',
                sidebarActiveBgColor: hs.sidebarActiveBgColor || (data.primary_color ? data.primary_color + '1a' : 'rgba(91, 67, 16, 0.1)'),
                enableTextStroke: hs.enableTextStroke || 'yes',
                displaySocialIcons: hs.displaySocialIcons !== false,
                showInstagram: hs.showInstagram !== false,
                showFacebook: hs.showFacebook !== false,
                showTwitter: hs.showTwitter !== false,
                showYoutube: hs.showYoutube !== false
            });

            // Extract lat and lng from mapEmbedUrl if present, to initialize map marker position
            const targetMapUrl = hs.mapEmbedUrl || '';
            const matchEmbed = targetMapUrl.match(/!2d([0-9.]+)!3d([0-9.]+)/) || targetMapUrl.match(/3d([0-9.]+)!4d([0-9.]+)/);
            if (matchEmbed) {
                // Determine layout coordinates order
                const first = parseFloat(matchEmbed[1]);
                const second = parseFloat(matchEmbed[2]);
                // Typically !2d is Longitude, !3d is Latitude
                if (targetMapUrl.includes('!2d') && targetMapUrl.includes('!3d')) {
                    setRestaurantMapLocation({ lat: second, lng: first });
                } else {
                    setRestaurantMapLocation({ lat: first, lng: second });
                }
            } else {
                // Fallback to default
                setRestaurantMapLocation({ lat: 13.0475, lng: 80.2472 });
            }
            setMapSearchInput(hs.locationAddress || data.address || '');

        } catch (err) {
            console.error('Failed to fetch homepage settings:', err);
            toast.error('Failed to load homepage settings');
        } finally {
            setLoading(false);
        }
    };

    const handleAddGalleryItem = () => {
        setHomeSettings(prev => ({
            ...prev,
            galleryItems: [...prev.galleryItems, {
                id: Date.now(),
                title: 'Signature Special Dish / Ambiance',
                category: 'mains',
                image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80',
                tag: 'Chef Special'
            }]
        }));
    };

    const handleRemoveGalleryItem = (id) => {
        setHomeSettings(prev => ({
            ...prev,
            galleryItems: prev.galleryItems.filter(item => item.id !== id)
        }));
    };

    const handleGalleryItemChange = (id, field, value) => {
        setHomeSettings(prev => ({
            ...prev,
            galleryItems: prev.galleryItems.map(item => item.id === id ? { ...item, [field]: value } : item)
        }));
    };

    const handleAddOperatingHour = () => {
        setHomeSettings(prev => ({
            ...prev,
            operatingHours: [...prev.operatingHours, { day: 'New Day Slot', time: '10:00 AM - 10:00 PM', status: 'Open' }]
        }));
    };

    const handleRemoveOperatingHour = (index) => {
        setHomeSettings(prev => ({
            ...prev,
            operatingHours: prev.operatingHours.filter((_, i) => i !== index)
        }));
    };

    const handleOperatingHourChange = (index, field, value) => {
        setHomeSettings(prev => {
            const updated = [...prev.operatingHours];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, operatingHours: updated };
        });
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        try {
            if (user?.role === 'franchise_admin') {
                await api.put('/api/franchises/branding', {
                    primaryColor,
                    secondaryColor,
                    logoUrl,
                    homeSettings
                });
                toast.success('Franchise branding published and propagated to all branches successfully!');
            } else {
                await Promise.all([
                    api.post('/api/restaurants/settings/homepage', { home_settings: homeSettings }),
                    api.put('/api/restaurants/config', { primaryColor, secondaryColor, logoUrl })
                ]);
                toast.success('Homepage content & text colors published successfully!');
            }
        } catch (err) {
            console.error('Failed to save homepage settings:', err);
            toast.error(err.response?.data?.message || 'Failed to update homepage content');
        } finally {
            setSaving(false);
        }
    };

    if (tenant?.website_type === 'custom') {
        return (
            <div className="max-w-3xl mx-auto py-16 px-6 text-center space-y-6">
                <div className="w-20 h-20 bg-amber-50 text-amber-600 rounded-3xl flex items-center justify-center mx-auto border border-amber-200 shadow-lg">
                    <Globe className="w-10 h-10" />
                </div>
                <div className="space-y-2">
                    <span className="text-xs font-black uppercase tracking-widest text-amber-600 bg-amber-100 px-3 py-1 rounded-full">Custom Website Portal Active</span>
                    <h2 className="text-3xl font-black text-slate-900">Homepage Editor Disabled</h2>
                    <p className="text-slate-500 text-sm leading-relaxed max-w-lg mx-auto">
                        Your restaurant uses a custom-designed web portal hosted separately. Template homepage customizer settings are disabled because your storefront code is managed via dedicated frontend deployments.
                    </p>
                </div>
                {tenant?.custom_frontend_url && (
                    <div className="pt-2">
                        <a 
                            href={tenant.custom_frontend_url} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold text-xs hover:bg-slate-800 transition-all shadow-md"
                        >
                            <span>Visit Your Custom Live Website</span>
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                )}
                <div className="pt-4">
                    <Link to="/admin" className="text-xs font-bold text-primary-600 hover:underline">
                        Return to Admin Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-20">
            {/* Top Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                <div>
                    <span className="text-primary-600 text-xs font-black uppercase tracking-widest bg-primary-50 px-3 py-1 rounded-full border border-primary-100">
                        {user?.role === 'franchise_admin' ? 'Brand Identity Manager' : 'Storefront Customizer'}
                    </span>
                    <h1 className="text-3xl font-black text-slate-900 mt-2 flex items-center gap-2">
                        <Layout className="w-8 h-8 text-primary-600" /> 
                        {user?.role === 'franchise_admin' ? 'Franchise Branding Configurator' : 'Homepage & Text Color Customizer'}
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">Configure brand accent colors, text colors, gallery photos, and location in real-time.</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-primary py-3.5 px-8 font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-xl shadow-primary-200"
                >
                    <Save className="w-4 h-4" />
                    <span>{saving ? (user?.role === 'franchise_admin' ? 'Propagating...' : 'Publishing...') : (user?.role === 'franchise_admin' ? 'Propagate Branding' : 'Publish Homepage')}</span>
                </button>
            </div>

            {/* Split Screen Layout */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
                
                {/* LEFT PANEL: EDITING FORM CONTROLS */}
                <div className="lg:col-span-6 space-y-6">
                    {/* Navigation Tabs */}
                    <div className="flex flex-wrap gap-2 bg-slate-200/60 p-1.5 rounded-2xl">
                        {[
                            { id: 'theme', label: 'Theme & Text Colors' },
                            { id: 'hero', label: 'Hero Banner' },
                            { id: 'features', label: 'Features Setup' },
                            { id: 'gallery', label: 'Gallery Showcase' },
                            { id: 'location', label: 'Location & Map' },
                            { id: 'hours', label: 'Operating Hours' },
                            { id: 'contact', label: 'Contact & Social' },
                            { id: 'legal', label: 'Legal Pages' }
                        ].map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                className={`px-3.5 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all flex-1 text-center ${
                                    activeTab === t.id
                                        ? 'bg-white text-slate-900 shadow-sm'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={handleSave} className="space-y-6">
                        {tenant?.franchise_id && user?.role !== 'franchise_admin' && ['theme', 'hero', 'features', 'gallery'].includes(activeTab) && (
                            <div className="bg-amber-50 border border-amber-250 rounded-3xl p-5 mb-6 flex items-start gap-3.5 text-amber-800">
                                <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5 text-amber-600" />
                                <div>
                                    <h4 className="font-bold text-sm">Franchise-Controlled Branding</h4>
                                    <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                                        This store is part of a franchise. Accent colors, logos, and general layout templates are managed centrally by the Franchise Admin and cannot be modified.
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="space-y-6">
                        {activeTab === 'theme' && (
                            <fieldset disabled={!!tenant?.franchise_id && user?.role !== 'franchise_admin'} className="space-y-6">
                                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                    <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                        <Palette className="w-6 h-6 text-primary-600" />
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Brand & Text Colors</h3>
                                            <p className="text-xs text-slate-400">Customize background accent colors and text typography colors.</p>
                                        </div>
                                    </div>
                                    
                                    {/* Company Logo */}
                                    <div className="space-y-3 pb-4">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">Company Logo</h4>
                                        <div className="flex items-center space-x-4 bg-slate-50 border border-slate-100 rounded-2xl p-4">
                                            {logoUrl ? (
                                                <div className="relative w-16 h-16 bg-white rounded-xl border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                    <img src={cleanLogoUrl(logoUrl)} alt="Logo preview" className="w-full h-full object-contain" />
                                                    <button type="button" onClick={() => setLogoUrl('')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-4 h-4 flex items-center justify-center text-[8px] font-bold">✕</button>
                                                </div>
                                            ) : (
                                                <label className="w-16 h-16 bg-white rounded-xl border-2 border-dashed border-slate-300 hover:border-primary-500 flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                                    <ImageIcon className="text-slate-400 w-5 h-5" />
                                                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                                                </label>
                                            )}
                                            <div className="flex-1 text-[11px] text-slate-400">
                                                {uploading ? (
                                                    <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary...</span>
                                                ) : (
                                                    <span>Upload brand logo (JPEG, PNG, max size 2 MB)</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Background Accent Colors */}
                                    <div className="space-y-3 pt-4 border-t border-slate-100">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">Brand Background Accent Colors</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Primary Brand Accent</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Secondary Dark Background</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div className="col-span-2 md:col-span-1">
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Enable Outline/Text Stroke</label>
                                                <select 
                                                    value={homeSettings.enableTextStroke || 'no'} 
                                                    onChange={(e) => setHomeSettings({ ...homeSettings, enableTextStroke: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-primary-500 h-[42px]"
                                                >
                                                    <option value="no">No (Disable Outline)</option>
                                                    <option value="yes">Yes (Enable Outline)</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Typography Text Colors */}
                                    <div className="space-y-3 pt-4 border-t border-slate-100">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider flex items-center gap-1.5"><Type className="w-4 h-4 text-indigo-600" /> Storefront Text Colors</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Hero Headline Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.heroTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, heroTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.heroTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, heroTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Hero Subtitle Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.heroSubtitleColor} onChange={(e) => setHomeSettings({ ...homeSettings, heroSubtitleColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.heroSubtitleColor} onChange={(e) => setHomeSettings({ ...homeSettings, heroSubtitleColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Section Headings Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.headingTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, headingTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.headingTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, headingTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">General Body Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.bodyTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, bodyTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.bodyTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, bodyTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Footer Headings Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.footerHeadingColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerHeadingColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.footerHeadingColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerHeadingColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Footer Text & Links Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.footerTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.footerTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Footer Background Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.footerBgColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerBgColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.footerBgColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerBgColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Footer Highlights & Icons Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.footerTimeColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerTimeColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.footerTimeColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerTimeColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Footer Secondary Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.footerSecondaryColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerSecondaryColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.footerSecondaryColor} onChange={(e) => setHomeSettings({ ...homeSettings, footerSecondaryColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dashboard & Button Text Colors */}
                                    <div className="space-y-3 pt-4 border-t border-slate-100">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider flex items-center gap-1.5"><Shield className="w-4 h-4 text-indigo-600" /> Dashboard & Button Colors (Readability Overrides)</h4>
                                        <p className="text-[11px] text-slate-450 leading-relaxed">
                                            If you choose a very light primary brand accent, use these to override text and active state colors in the admin panel and store buttons for legibility.
                                        </p>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Primary Button Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.primaryButtonTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, primaryButtonTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.primaryButtonTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, primaryButtonTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Active Sidebar Item Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.sidebarActiveTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, sidebarActiveTextColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.sidebarActiveTextColor} onChange={(e) => setHomeSettings({ ...homeSettings, sidebarActiveTextColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Active Sidebar Item Background Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input type="color" value={homeSettings.sidebarActiveBgColor} onChange={(e) => setHomeSettings({ ...homeSettings, sidebarActiveBgColor: e.target.value })} className="w-10 h-10 rounded-xl border-none cursor-pointer shadow-inner" />
                                                    <input type="text" value={homeSettings.sidebarActiveBgColor} onChange={(e) => setHomeSettings({ ...homeSettings, sidebarActiveBgColor: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-mono uppercase font-bold" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* TAB 2: HERO BANNER */}
                        {activeTab === 'hero' && (
                            <fieldset disabled={!!tenant?.franchise_id && user?.role !== 'franchise_admin'} className="contents">
                                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                    <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                        <Sparkles className="w-6 h-6 text-amber-500" />
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Hero Header Banner</h3>
                                            <p className="text-xs text-slate-400">The top welcome banner on your homepage.</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Headline Title</label>
                                            <input
                                                type="text"
                                                value={homeSettings.heroTitle}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, heroTitle: e.target.value })}
                                                placeholder="e.g. Welcome to Savory Bites"
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-bold text-slate-900 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Tagline Subtitle</label>
                                            <textarea
                                                rows="2"
                                                value={homeSettings.heroSubtitle}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, heroSubtitle: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-medium outline-none"
                                            ></textarea>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Hero Background Image</label>
                                            <div className="flex items-center space-x-4 bg-slate-50 border border-slate-200 rounded-2xl p-4">
                                                {homeSettings.heroImage ? (
                                                    <div className="relative w-20 h-16 bg-white rounded-xl border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                        <img src={homeSettings.heroImage} alt="Hero preview" className="w-full h-full object-cover" />
                                                        <button type="button" onClick={() => setHomeSettings(prev => ({ ...prev, heroImage: '' }))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-4 h-4 flex items-center justify-center text-[8px] font-bold">✕</button>
                                                    </div>
                                                ) : (
                                                    <label className="w-20 h-16 bg-white rounded-xl border-2 border-dashed border-slate-300 hover:border-primary-500 flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                                        <ImageIcon className="text-slate-400 w-5 h-5" />
                                                        <input type="file" accept="image/*" onChange={handleHeroImageUpload} className="hidden" disabled={uploading} />
                                                    </label>
                                                )}
                                                <div className="flex-1 text-[11px] text-slate-400">
                                                    {uploading ? (
                                                        <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary...</span>
                                                    ) : (
                                                        <span>Upload website hero cover (JPEG, PNG, max size 2 MB)</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* TAB 3: FEATURES SETUP */}
                        {activeTab === 'features' && (
                            <fieldset disabled={!!tenant?.franchise_id && user?.role !== 'franchise_admin'} className="contents">
                                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                    <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                        <Truck className="w-6 h-6 text-primary-600" />
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Features Setup</h3>
                                            <p className="text-xs text-slate-400">Customize your 3 feature highlight cards (e.g. 30-Min Fast Delivery).</p>
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-3">
                                            <h4 className="text-xs font-black uppercase text-primary-600 tracking-wider flex items-center gap-2"><Clock className="w-4 h-4" /> Feature 1 (Delivery)</h4>
                                            <input
                                                type="text"
                                                value={homeSettings.feature1Title}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature1Title: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <input
                                                type="text"
                                                value={homeSettings.feature1Desc}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature1Desc: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs"
                                            />
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-3">
                                            <h4 className="text-xs font-black uppercase text-amber-600 tracking-wider flex items-center gap-2"><Shield className="w-4 h-4" /> Feature 2 (Quality)</h4>
                                            <input
                                                type="text"
                                                value={homeSettings.feature2Title}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature2Title: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <input
                                                type="text"
                                                value={homeSettings.feature2Desc}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature2Desc: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs"
                                            />
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-3">
                                            <h4 className="text-xs font-black uppercase text-indigo-600 tracking-wider flex items-center gap-2"><Truck className="w-4 h-4" /> Feature 3 (Offer)</h4>
                                            <input
                                                type="text"
                                                value={homeSettings.feature3Title}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature3Title: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <input
                                                type="text"
                                                value={homeSettings.feature3Desc}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, feature3Desc: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* TAB 4: GALLERY SHOWCASE */}
                        {activeTab === 'gallery' && (
                            <fieldset disabled={!!tenant?.franchise_id && user?.role !== 'franchise_admin'} className="contents">
                                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                    <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                                        <div className="flex items-center space-x-3">
                                            <Utensils className="w-6 h-6 text-indigo-600" />
                                            <div>
                                                <h3 className="text-lg font-black text-slate-900">Gallery Showcase & Images</h3>
                                                <p className="text-xs text-slate-400">Manage titles, descriptions, and food/ambiance images.</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddGalleryItem}
                                            className="px-3.5 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-xl text-xs font-black border border-indigo-200 flex items-center gap-1"
                                        >
                                            <Plus className="w-4 h-4" /> Add Photo
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-1.5">Gallery Badge Text</label>
                                            <input
                                                type="text"
                                                value={homeSettings.galleryBadge}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, galleryBadge: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-1.5">Gallery Section Heading</label>
                                            <input
                                                type="text"
                                                value={homeSettings.galleryTitle}
                                                onChange={(e) => setHomeSettings({ ...homeSettings, galleryTitle: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm font-black"
                                            />
                                        </div>
                                    </div>

                                    {/* Customize Filter Categories */}
                                    <div className="space-y-4 pt-4 border-t border-slate-100">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">Customize Gallery Filter Tabs</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {(homeSettings.galleryCategories || defaultGalleryCategories).map((cat) => (
                                                <div key={cat.id}>
                                                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5">{cat.icon} {cat.id} Tab</label>
                                                    <input
                                                        type="text"
                                                        value={cat.label}
                                                        onChange={(e) => {
                                                            const updatedCats = (homeSettings.galleryCategories || defaultGalleryCategories).map(c => 
                                                                c.id === cat.id ? { ...c, label: e.target.value } : c
                                                            );
                                                            setHomeSettings({ ...homeSettings, galleryCategories: updatedCats });
                                                        }}
                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-4 pt-4 border-t border-slate-100">
                                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">Gallery Photos List ({homeSettings.galleryItems.length})</h4>
                                        {homeSettings.galleryItems.map((item) => (
                                            <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-3 relative group">
                                                <div className="flex justify-between items-center gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <img src={item.image} alt={item.title} className="w-12 h-12 rounded-xl object-cover border border-slate-200" />
                                                        <span className="text-xs font-bold text-slate-800 truncate max-w-[180px]">{item.title}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveGalleryItem(item.id)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input
                                                        type="text"
                                                        value={item.title}
                                                        onChange={(e) => handleGalleryItemChange(item.id, 'title', e.target.value)}
                                                        placeholder="Photo Title"
                                                        className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-bold"
                                                    />
                                                    <select
                                                        value={item.category}
                                                        onChange={(e) => handleGalleryItemChange(item.id, 'category', e.target.value)}
                                                        className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-bold"
                                                    >
                                                        {(homeSettings.galleryCategories || defaultGalleryCategories).map((cat) => (
                                                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2 items-center">
                                                    <div className="col-span-2 flex items-center space-x-2 bg-white border border-slate-200 rounded-xl p-1.5">
                                                        {item.image ? (
                                                            <div className="relative w-9 h-9 bg-slate-50 rounded-lg overflow-hidden border border-slate-100 flex-shrink-0 flex items-center justify-center">
                                                                <img src={item.image} alt="Preview" className="w-full h-full object-cover" />
                                                                <button type="button" onClick={() => handleGalleryItemChange(item.id, 'image', '')} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-3.5 h-3.5 flex items-center justify-center text-[7px] font-bold">✕</button>
                                                            </div>
                                                        ) : (
                                                            <label className="w-9 h-9 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 hover:border-primary-500 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                                                <ImageIcon className="text-slate-400 w-3.5 h-3.5" />
                                                                <input type="file" accept="image/*" onChange={(e) => handleGalleryImageUpload(e, item.id)} className="hidden" disabled={uploading} />
                                                            </label>
                                                        )}
                                                        <span className="text-[10px] text-slate-400 truncate flex-1 pl-1">
                                                            {uploading ? 'Uploading...' : (item.image ? 'Image Uploaded' : 'Upload Image (2MB)')}
                                                        </span>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={item.tag}
                                                        onChange={(e) => handleGalleryItemChange(item.id, 'tag', e.target.value)}
                                                        placeholder="Badge Tag"
                                                        className="bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-bold h-full"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* TAB 5: LOCATION & MAP CONFIG */}
                        {activeTab === 'location' && (
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                    <MapPin className="w-6 h-6 text-emerald-600" />
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Location & Google Maps Config</h3>
                                        <p className="text-xs text-slate-400">Configure your physical address and interactive Google Maps iframe.</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    {/* Search Box with Autocomplete Suggestions */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Search Restaurant Location</label>
                                        {isMapLoaded ? (
                                            <div className="relative">
                                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 z-10" />
                                                <Autocomplete
                                                    onLoad={(ac) => setAutocompleteInstance(ac)}
                                                    onPlaceChanged={handlePlaceSelected}
                                                    options={{
                                                        componentRestrictions: { country: 'in' },
                                                        types: ['geocode', 'establishment']
                                                    }}
                                                >
                                                    <input
                                                        type="text"
                                                        value={mapSearchInput}
                                                        onChange={(e) => setMapSearchInput(e.target.value)}
                                                        placeholder="Type restaurant name, area, or landmark..."
                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 pl-9 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                                    />
                                                </Autocomplete>
                                                <p className="text-[10px] text-slate-400 mt-1.5">Start typing to see suggestions. Pick one to pin it on the map below.</p>
                                            </div>
                                        ) : (
                                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs text-slate-400">Loading Google Maps...</div>
                                        )}
                                    </div>

                                    {/* Interactive Map with Draggable Marker */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Fine-tune Location on Map</label>
                                        <div className="relative rounded-2xl overflow-hidden border border-slate-200">
                                            {isMapLoaded ? (
                                                <>
                                                    <GoogleMap
                                                        mapContainerStyle={mapContainerStyle}
                                                        center={restaurantMapLocation || { lat: 13.0475, lng: 80.2472 }}
                                                        zoom={15}
                                                        onClick={handleMapClick}
                                                        onLoad={(map) => setMapInstance(map)}
                                                        options={{
                                                            streetViewControl: false,
                                                            mapTypeControl: false,
                                                            fullscreenControl: false
                                                        }}
                                                    >
                                                        {restaurantMapLocation && (
                                                            <Marker
                                                                position={restaurantMapLocation}
                                                                draggable={true}
                                                                onDragEnd={(e) => handleMapClick(e)}
                                                            />
                                                        )}
                                                    </GoogleMap>
                                                    <button
                                                        type="button"
                                                        onClick={handleCenterOnMe}
                                                        className="absolute bottom-3 right-3 bg-white p-2 rounded-full shadow-lg hover:bg-slate-100 transition-colors z-10"
                                                        title="Use my current location"
                                                    >
                                                        <Crosshair className="w-4 h-4 text-slate-700" />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="h-[280px] flex items-center justify-center bg-slate-50 text-xs text-slate-400">Loading Google Maps...</div>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> Wrong spot? Click on the map or drag the marker to fix the exact location.
                                        </p>
                                    </div>

                                    {/* Auto-filled Address (editable) */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Restaurant Address (auto-filled, editable)</label>
                                        <textarea
                                            rows="2"
                                            value={homeSettings.locationAddress}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, locationAddress: e.target.value })}
                                            placeholder="Address will auto-fill when you select a location above"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs font-bold"
                                        ></textarea>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">FSSAI License Number (Footer Display)</label>
                                        <input
                                            type="text"
                                            value={homeSettings.fssaiLicense}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, fssaiLicense: e.target.value })}
                                            placeholder="e.g. 12422020000145"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs font-bold"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 6: DYNAMIC OPERATING HOURS */}
                        {activeTab === 'hours' && (
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                                    <div className="flex items-center space-x-3">
                                        <Clock className="w-6 h-6 text-primary-600" />
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900">Operating Hours Schedule</h3>
                                            <p className="text-xs text-slate-400">Add or remove operating hour slots (3 is not mandatory).</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAddOperatingHour}
                                        className="px-3 py-1.5 bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-xl text-xs font-bold border border-primary-200 flex items-center gap-1"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Add Slot
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {homeSettings.operatingHours.map((oh, index) => (
                                        <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 flex flex-col sm:flex-row gap-3 items-center">
                                            <input
                                                type="text"
                                                value={oh.day}
                                                onChange={(e) => handleOperatingHourChange(index, 'day', e.target.value)}
                                                placeholder="e.g. Monday - Thursday"
                                                className="w-full sm:w-1/3 bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <input
                                                type="text"
                                                value={oh.time}
                                                onChange={(e) => handleOperatingHourChange(index, 'time', e.target.value)}
                                                placeholder="e.g. 11:00 AM - 10:30 PM"
                                                className="w-full sm:w-1/3 bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <input
                                                type="text"
                                                value={oh.status}
                                                onChange={(e) => handleOperatingHourChange(index, 'status', e.target.value)}
                                                placeholder="Status Badge (Open/Open Late)"
                                                className="w-full sm:w-1/4 bg-white border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveOperatingHour(index)}
                                                className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* TAB 7: CONTACT & SOCIAL */}
                        {activeTab === 'contact' && (
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                    <Phone className="w-6 h-6 text-indigo-600" />
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Contact & Social Links</h3>
                                        <p className="text-xs text-slate-400">Support contact info and social media profile URLs.</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200/60 rounded-2xl">
                                    <div>
                                        <p className="text-xs font-black text-slate-800">Display Social Media Channels</p>
                                        <p className="text-[10px] text-slate-400">Toggle whether social links display on the storefront homepage footer.</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={homeSettings.displaySocialIcons}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, displaySocialIcons: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                                    </label>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Display Phone / WhatsApp</label>
                                        <input
                                            type="text"
                                            value={homeSettings.contactPhone}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, contactPhone: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Display Email</label>
                                        <input
                                            type="email"
                                            value={homeSettings.contactEmail}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, contactEmail: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                        />
                                    </div>
                                </div>
                                 <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                                    <div className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-black text-slate-500 uppercase">Instagram URL</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] font-semibold text-slate-400">Show Icon</span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={homeSettings.showInstagram !== false} 
                                                    onChange={(e) => setHomeSettings({ ...homeSettings, showInstagram: e.target.checked })} 
                                                    className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" 
                                                />
                                            </div>
                                        </div>
                                        <input type="url" value={homeSettings.instagramUrl} onChange={(e) => setHomeSettings({ ...homeSettings, instagramUrl: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-mono" placeholder="https://instagram.com/yourprofile" />
                                    </div>
                                    <div className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-black text-slate-500 uppercase">Facebook URL</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] font-semibold text-slate-400">Show Icon</span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={homeSettings.showFacebook !== false} 
                                                    onChange={(e) => setHomeSettings({ ...homeSettings, showFacebook: e.target.checked })} 
                                                    className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" 
                                                />
                                            </div>
                                        </div>
                                        <input type="url" value={homeSettings.facebookUrl} onChange={(e) => setHomeSettings({ ...homeSettings, facebookUrl: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-mono" placeholder="https://facebook.com/yourpage" />
                                    </div>
                                    <div className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-black text-slate-500 uppercase">Twitter URL</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] font-semibold text-slate-400">Show Icon</span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={homeSettings.showTwitter !== false} 
                                                    onChange={(e) => setHomeSettings({ ...homeSettings, showTwitter: e.target.checked })} 
                                                    className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" 
                                                />
                                            </div>
                                        </div>
                                        <input type="url" value={homeSettings.twitterUrl} onChange={(e) => setHomeSettings({ ...homeSettings, twitterUrl: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-mono" placeholder="https://x.com/yourhandle" />
                                    </div>
                                    <div className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-black text-slate-500 uppercase">YouTube URL</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] font-semibold text-slate-400">Show Icon</span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={homeSettings.showYoutube !== false} 
                                                    onChange={(e) => setHomeSettings({ ...homeSettings, showYoutube: e.target.checked })} 
                                                    className="rounded border-slate-350 text-indigo-650 focus:ring-indigo-500" 
                                                />
                                            </div>
                                        </div>
                                        <input type="url" value={homeSettings.youtubeUrl} onChange={(e) => setHomeSettings({ ...homeSettings, youtubeUrl: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs font-mono" placeholder="https://youtube.com/c/yourchannel" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 8: LEGAL PAGES */}
                        {activeTab === 'legal' && (
                            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center space-x-3 border-b border-slate-100 pb-4">
                                    <Shield className="w-6 h-6 text-emerald-600" />
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Legal Pages</h3>
                                        <p className="text-xs text-slate-400">Customize the text for your Privacy Policy, Terms of Service, and Refund Policy shown in the footer.</p>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Privacy Policy</label>
                                        <textarea
                                            rows="8"
                                            value={homeSettings.privacyPolicy}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, privacyPolicy: e.target.value })}
                                            placeholder="Enter your Privacy Policy content here. Describe what data you collect, how you use it, and how customers can contact you regarding their data..."
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs outline-none resize-y font-mono leading-relaxed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Terms of Service</label>
                                        <textarea
                                            rows="8"
                                            value={homeSettings.termsOfService}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, termsOfService: e.target.value })}
                                            placeholder="Enter your Terms of Service here. Describe the rules customers agree to when using your platform, ordering food, making payments, etc..."
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs outline-none resize-y font-mono leading-relaxed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Refund & Cancellation Policy</label>
                                        <textarea
                                            rows="8"
                                            value={homeSettings.refundPolicy}
                                            onChange={(e) => setHomeSettings({ ...homeSettings, refundPolicy: e.target.value })}
                                            placeholder="Enter your Refund & Cancellation Policy here. Describe your refund windows, cancellation cut-off times, how refunds are processed, etc..."
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs outline-none resize-y font-mono leading-relaxed"
                                        />
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-800">
                                        <strong>Note:</strong> These policies will appear as clickable links in your storefront footer, opening as a modal popup for customers to read. Leave a field empty to hide that policy link.
                                    </div>
                                </div>
                            </div>
                        )}

                        </div>

                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn-primary py-4 px-10 font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-xl shadow-primary-200"
                            >
                                <Save className="w-4 h-4" />
                                <span>{saving ? 'Publishing Changes...' : 'Publish Homepage Changes'}</span>
                            </button>
                        </div>
                    </form>
                </div>

                {/* RIGHT PANEL: LIVE PREVIEW STOREFRONT CARD */}
                <div className="lg:col-span-6 sticky top-24 space-y-4">
                    <div className="flex justify-between items-center bg-slate-900 text-white p-4 rounded-t-3xl border-b border-slate-800">
                        <span className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-widest">
                            <Eye className="w-4 h-4" /> Live Storefront Preview
                        </span>
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2.5 py-0.5 rounded-full font-bold">Real-time Sync</span>
                    </div>

                    <div className="bg-slate-100 rounded-b-3xl overflow-hidden shadow-2xl border border-slate-200 max-h-[80vh] overflow-y-auto custom-scrollbar text-slate-800 text-xs">
                        {/* Simulated Storefront Hero */}
                        <div className="relative p-8 text-center overflow-hidden" style={{ backgroundColor: primaryColor }}>
                            <div className="absolute inset-0 bg-cover bg-center opacity-20 mix-blend-overlay" style={{ backgroundImage: `url('${homeSettings.heroImage}')` }} />
                            <div className="relative z-10 space-y-3">
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-[10px] font-bold uppercase tracking-widest text-white">
                                    <Utensils className="w-3 h-3" style={{ color: primaryColor || '#fbbf24' }} /> Premium Culinary
                                </span>
                                <h2 className="text-2xl font-black" style={{ color: homeSettings.heroTextColor }}>{homeSettings.heroTitle || `Welcome to ${restaurantName}`}</h2>
                                <p className="text-xs max-w-md mx-auto leading-relaxed" style={{ color: homeSettings.heroSubtitleColor }}>{homeSettings.heroSubtitle}</p>
                                <div className="pt-2">
                                    <button type="button" className="bg-white text-slate-900 px-5 py-2.5 rounded-xl font-black text-xs shadow-md">Order Now</button>
                                </div>
                            </div>
                        </div>

                        {/* Simulated Features */}
                        <div className="p-4 grid grid-cols-3 gap-2 -mt-4 relative z-20">
                            <div className="bg-white p-3 rounded-2xl shadow-md border border-slate-100 text-center">
                                <Clock className="w-4 h-4 mx-auto mb-1" style={{ color: primaryColor }} />
                                <h4 className="font-bold text-[11px] truncate" style={{ color: homeSettings.headingTextColor }}>{homeSettings.feature1Title}</h4>
                                <p className="text-[9px] truncate mt-0.5" style={{ color: homeSettings.bodyTextColor }}>{homeSettings.feature1Desc}</p>
                            </div>
                            <div className="bg-white p-3 rounded-2xl shadow-md border border-slate-100 text-center">
                                <Shield className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                                <h4 className="font-bold text-[11px] truncate" style={{ color: homeSettings.headingTextColor }}>{homeSettings.feature2Title}</h4>
                                <p className="text-[9px] truncate mt-0.5" style={{ color: homeSettings.bodyTextColor }}>{homeSettings.feature2Desc}</p>
                            </div>
                            <div className="bg-white p-3 rounded-2xl shadow-md border border-slate-100 text-center">
                                <Truck className="w-4 h-4 mx-auto text-indigo-600 mb-1" />
                                <h4 className="font-bold text-[11px] truncate" style={{ color: homeSettings.headingTextColor }}>{homeSettings.feature3Title}</h4>
                                <p className="text-[9px] truncate mt-0.5" style={{ color: homeSettings.bodyTextColor }}>{homeSettings.feature3Desc}</p>
                            </div>
                        </div>

                        {/* Simulated Gallery Section */}
                        <div className="p-6 text-center space-y-3 bg-white mt-2">
                            <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: primaryColor, backgroundColor: `${primaryColor}10`, borderColor: `${primaryColor}30` }}>{homeSettings.galleryBadge}</span>
                            <h3 className="text-lg font-black" style={{ color: homeSettings.headingTextColor }}>{homeSettings.galleryTitle}</h3>
                            <p className="text-[10px] max-w-xs mx-auto" style={{ color: homeSettings.bodyTextColor }}>{homeSettings.galleryDesc}</p>
                            
                            <div className="grid grid-cols-2 gap-2 text-left pt-2">
                                {homeSettings.galleryItems.slice(0, 4).map((g) => (
                                    <div key={g.id} className="relative rounded-xl overflow-hidden h-20 border border-slate-100 group">
                                        <img src={g.image} alt={g.title} className="w-full h-full object-cover" />
                                        <span className="absolute bottom-1 left-1 bg-slate-900/80 text-white text-[8px] font-bold px-1.5 py-0.5 rounded truncate max-w-[90%]">{g.title}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Simulated Location & Map */}
                        <div className="p-4 text-white space-y-2 mt-2" style={{ backgroundColor: secondaryColor }}>
                            <h4 className="font-bold text-xs text-emerald-400 uppercase flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Store Location</h4>
                            <p className="text-[10px] text-slate-300">{homeSettings.locationAddress}</p>
                        </div>

                        {/* Simulated Footer */}
                        <div className="p-6 text-[10px] space-y-3 text-left border-t border-slate-800" style={{ backgroundColor: homeSettings.footerBgColor || secondaryColor, color: homeSettings.footerTextColor }}>
                            <div className="flex justify-between items-center border-b border-slate-800/60 pb-2">
                                <span className="font-black text-xs flex items-center" style={{ color: homeSettings.footerHeadingColor }}>
                                    {logoUrl && <img src={cleanLogoUrl(logoUrl)} alt="Logo" className="h-6 w-auto inline-block mr-2" />}
                                    {restaurantName}
                                </span>
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded border" style={{ color: primaryColor || '#fbbf24', backgroundColor: primaryColor ? `${primaryColor}10` : 'rgba(251,191,36,0.1)', borderColor: primaryColor ? `${primaryColor}30` : 'rgba(251,191,36,0.2)' }}>FSSAI: {homeSettings.fssaiLicense}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <h5 className="font-bold uppercase text-[9px] mb-1" style={{ color: homeSettings.footerHeadingColor }}>Quick Links</h5>
                                    <p className="opacity-80">Home • Menu • Contact</p>
                                </div>
                                <div>
                                    <h5 className="font-bold uppercase text-[9px] mb-1" style={{ color: homeSettings.footerHeadingColor }}>Contact</h5>
                                    <p className="opacity-80 truncate">{homeSettings.contactPhone}</p>
                                </div>
                            </div>
                            <p className="text-center text-[9px] opacity-70 pt-2 border-t border-slate-800/60">© {restaurantName}. All Rights Reserved.</p>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
}
